class InteractiveCategoryList extends HTMLElement {
  constructor() {
    super();

    this.items = null;
    this.images = null;
    this.section = null;
    this.stickyContent = null;
    this.container = null;

    this.scrollActiveIndex = 0;
    this.hoveredIndex = null;
    this.lastScrollY = window.scrollY;
    this.headerHeight = 0;

    this.isDesktop = window.matchMedia("(min-width: 768px)");
    this.intersectionObserver = null;
    this.resizeObserver = null;

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onIntersection = this.onIntersection.bind(this);
  }

  connectedCallback() {
    this.initDOM();
    this.initLayout();
    this.initEventListeners();
    this.setupObservers();
    this.setActiveItem(0);
  }

  initDOM() {
    this.items = this.querySelectorAll("[data-category-index]");
    this.images = document.querySelectorAll("[data-image-index]");
    this.section = this.closest(".scrolling-animation");
    this.stickyContent = this.section?.querySelector(".scrolling-animation__desktop");
    this.container = this.section?.querySelector(".scrolling-animation__container");
  }

  initLayout() {
    this.calculateHeaderHeight();
    this.updateSectionHeight();
  }

  calculateHeaderHeight() {
    const header = document.querySelector("header, .header, [data-header]");
    this.headerHeight = header?.offsetHeight || 0;
    this.stickyContent?.style.setProperty("--sticky-offset", `${this.headerHeight + 48}px`);
  }

  updateSectionHeight() {
    const totalHeight = window.innerHeight * this.items.length * 1;
    this.container.style.height = `${totalHeight}px`;
    this.container.style.position = "relative";
  }

  setupObservers() {
    this.intersectionObserver?.disconnect();

    this.intersectionObserver = new IntersectionObserver(this.onIntersection, {
      root: null,
      threshold: 0,
    });
    this.intersectionObserver.observe(this.section);

    if (!this.resizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.calculateHeaderHeight();
        this.updateSectionHeight();
      });
      this.resizeObserver.observe(document.body);
    }
  }

  onIntersection(entries) {
    const isIntersecting = entries[0].isIntersecting && this.isDesktop.matches;

    if (isIntersecting) {
      window.addEventListener("scroll", this.onScroll, { passive: true });
      this.onScroll();
    } else {
      this.removeScrollListeners();
      this.stickyContent?.classList.remove("is-fixed", "is-abs-bottom");
    }
  }

  removeScrollListeners() {
    window.removeEventListener("scroll", this.onScroll);
  }

  onScroll() {
    if (!this.section || !this.stickyContent || !this.container) return;

    const positions = this.calculatePositions();
    this.updateStickyPosition(positions);

    const { progress, newIndex } = this.calculateScrollProgress();
    this.scrollActiveIndex = newIndex;

    if (this.hoveredIndex === null) {
      this.setActiveItem(newIndex);
      this.style.setProperty("--scroll-progress", `${progress * 100}%`);
    }
  }

  calculatePositions() {
    return {
      section: this.section.getBoundingClientRect(),
      container: this.container.getBoundingClientRect(),
      sticky: this.stickyContent.getBoundingClientRect(),
    };
  }

  updateStickyPosition({ section, container, sticky }) {
    const isNormal = section.top > this.headerHeight;
    const spaceBelow = container.bottom - this.headerHeight;
    const hasSpace = spaceBelow >= sticky.height;

    this.stickyContent.classList.toggle("is-fixed", !isNormal && hasSpace);
    this.stickyContent.classList.toggle("is-abs-bottom", !isNormal && !hasSpace);
  }

  calculateScrollProgress() {
    const sectionRect = this.section.getBoundingClientRect();
    const scrolledIntoSection = this.headerHeight - sectionRect.top;
    const maxScroll = sectionRect.height - window.innerHeight;
    const progress = Math.max(0, Math.min(1, scrolledIntoSection / maxScroll));
    const newIndex = Math.min(this.items.length - 1, Math.floor(progress * this.items.length));

    return { progress, newIndex };
  }

  updateScrollProgress() {
    if (!this.section) return;
    const { progress } = this.calculateScrollProgress();
    this.style.setProperty("--scroll-progress", `${progress * 100}%`);
  }

  initEventListeners() {
    this.isDesktop.addEventListener("change", this.onResize);
  }

  onResize(e) {
    if (e.matches) {
      this.initLayout();
      this.setupObservers();
    } else {
      this.resetMobileLayout();
      this.stickyContent?.classList.remove("is-fixed", "is-abs-bottom");
      this.removeScrollListeners();
      this.hoveredIndex = null;
    }
  }

  setActiveItem(index) {
    const indexStr = index.toString();

    this.items.forEach((item) => item.classList.toggle("active", item.dataset.categoryIndex === indexStr));
    this.images.forEach((image) => image.classList.toggle("active", image.dataset.imageIndex === indexStr));

    if (this.items.length > 1) {
      const progress = (index / (this.items.length - 1)) * 100;

      requestAnimationFrame(() => {
        this.style.setProperty("--item-progress", `${progress}%`);
      });
    }
  }

  cleanup() {
    this.intersectionObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.stickyContent?.classList.remove("is-fixed", "is-abs-bottom");
    this.removeScrollListeners();
    this.hoveredIndex = null;
  }

  disconnectedCallback() {
    this.cleanup();
    this.isDesktop.removeEventListener("change", this.onResize);
  }
}

if (!customElements.get("interactive-category-list")) {
  customElements.define("interactive-category-list", InteractiveCategoryList);
}
